<template>
  <div>
    <list-table
      :records="records"
      :columns="tableColumns"
      :total-records="totalRecords"
      :current-page-number.sync="currentPage"
      :search-filter.sync="search"
      :show-search="true"
      :show-filter-by-default="true"
    >
      <template #action_button>
        <b-button
          v-if="can('api_clients:CanList')"
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="outline-primary"
          class="mr-1"
          :to="{ name: 'api-clients-usage' }"
        >
          <feather-icon icon="ActivityIcon" class="mr-50" />
          <span class="align-middle text-lg">Usage</span>
        </b-button>

        <b-button
          v-if="can('api_clients:CanAddNew')"
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="primary"
          :to="{ name: 'api-clients-new' }"
        >
          <span class="align-middle text-lg">Add Client</span>
        </b-button>
      </template>

      <template #cell(title)="data">
        <div class="font-weight-bold">{{ data.item.title }}</div>
        <small class="text-muted">{{ data.item.client_id }}</small>
      </template>

      <template #cell(contact)="data">
        <div>{{ data.item.contact_name }}</div>
        <small class="text-muted">{{ data.item.contact_email }}</small>
      </template>

      <template #cell(user_provisioning)="data">
        <b-badge
          pill
          :variant="`light-${resolveProvisioningVariant(data.item.user_provisioning)}`"
          class="text-capitalize"
        >
          {{ resolveProvisioningText(data.item.user_provisioning) }}
        </b-badge>
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="`light-${resolveStatusVariant(data.item.status)}`"
          class="text-capitalize"
        >
          {{ data.item.status }}
        </b-badge>
      </template>

      <template #cell(actions)="data">
        <b-dropdown
          variant="link"
          no-caret
          :right="$store.state.appConfig.isRTL"
        >
          <template #button-content>
            <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body" />
          </template>

          <b-dropdown-item
            v-if="can('api_clients:CanView')"
            :to="{ name: 'api-clients-view', params: { id: data.item._id } }"
          >
            <feather-icon icon="EyeIcon" />
            <span class="align-middle ml-50">View / Edit</span>
          </b-dropdown-item>

          <b-dropdown-item
            v-if="can('api_clients:CanView')"
            :to="{ name: 'api-clients-usage-view', params: { id: data.item._id } }"
          >
            <feather-icon icon="ActivityIcon" />
            <span class="align-middle ml-50">Usage</span>
          </b-dropdown-item>

          <b-dropdown-item
            v-if="can('api_clients:CanDelete') && data.item.status !== 'revoked'"
            @click="onRevoke(data.item._id)"
          >
            <feather-icon icon="SlashIcon" />
            <span class="align-middle ml-50">Revoke</span>
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </list-table>
  </div>
</template>

<script>
import Ripple from 'vue-ripple-directive'
import ListTable from '@core/components/ListTable/ListTable.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import { debounce, get } from 'lodash'
import { formatDate } from '@core/utils/utils'
import { BButton, BBadge, BDropdown, BDropdownItem } from 'bootstrap-vue'

const watchHandler = {
  handler() {
    this.debounceFetchAllData()
  },
  immediate: false,
}

const watchHandlerRequiresPageReset = {
  handler() {
    this.currentPage = 1
    this.debounceFetchAllData()
  },
  immediate: false,
}

export default {
  components: {
    BBadge,
    BButton,
    ListTable,
    BDropdown,
    BDropdownItem,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      loading: false,
      records: [],

      search: '',
      debounceFetchAllData: null,

      tableColumns: [
        { key: 'title', label: 'Client', sortable: false },
        { key: 'contact', label: 'Contact', sortable: false },
        { key: 'user_provisioning', label: 'Accounts', sortable: false },
        { key: 'status', sortable: false },
        {
          key: 'created',
          sortable: false,
          formatter: val => `${formatDate(val, 'dd-MM-yy h:m:s a')}`,
          label: 'Created',
          class: 'width-200',
        },
        { key: 'actions' },
      ],
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.getters['navigation/recordsPerPage']
      },
      set(value) {
        this.$store.commit('navigation/UPDATE_RECORDS_PER_PAGE', value)
      },
    },
    currentPage: {
      get() {
        return this.$store.getters['navigation/currentPage']
      },
      set(value) {
        this.$store.commit('navigation/UPDATE_CURRENT_PAGE_NUMBER', value)
      },
    },
    totalRecords: {
      get() {
        return this.$store.getters['navigation/totalRecords']
      },
      set(value) {
        this.$store.commit('navigation/UPDATE_TOTAL_RECORDS', value)
      },
    },
  },
  watch: {
    currentPage: watchHandler,
    search: watchHandlerRequiresPageReset,
    perPage: watchHandlerRequiresPageReset,
  },
  created() {
    this.debounceFetchAllData = debounce(this.fetchAllData, 500)

    const { search, page = 1, limit = this.perPage } = this.$route.query

    this.search = search
    this.perPage = +limit
    this.currentPage = +page

    this.fetchAllData()
  },
  methods: {
    resolveStatusVariant(status) {
      if (status === 'active') return 'success'
      if (status === 'suspended') return 'warning'
      return 'danger'
    },
    resolveProvisioningVariant(mode) {
      if (mode === 'full') return 'danger'
      if (mode === 'linked') return 'warning'
      return 'success'
    },
    resolveProvisioningText(mode) {
      if (mode === 'full') return 'May create'
      if (mode === 'linked') return 'May link'
      return 'Profile only'
    },
    async fetchAllData() {
      try {
        this.loading = true

        const query = {
          limit: this.perPage,
          search: this.search,
          page: this.currentPage,
        }
        await this.$router.push({ query }).catch(() => {})

        const request = await this.useJwt().mainService.apiClients.list(query)
        const { data, pagination } = request.data

        this.totalRecords = get(pagination, 'totalRecords', 0)
        this.perPage = get(pagination, 'limit', this.perPage)
        this.records = data
      } catch (error) {
        const error_message = get(error, 'response.data.message') || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message,
          },
        })
      } finally {
        this.loading = false
      }
    },
    async onRevoke(id) {
      try {
        const result = await this.$swal({
          title: 'Revoke this client?',
          text: 'Its credentials stop working immediately and every active token is revoked.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, revoke it.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        })

        if (!result.value) return

        this.loading = true
        await this.useJwt().mainService.apiClients.delete(id)
        await this.fetchAllData()

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: 'Client revoked successfully',
          },
        })
      } catch (error) {
        const error_message = get(error, 'response.data.message') || error.message

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message,
            },
          })
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
